import { LianeUserFollowUpDetailDto } from '@/__generated';
import CreateBtn from '@/components/app/CreateBtn';
import ZTable, { IColumnType, IFilterItem, ITableRef } from '@/components/ZTable';
import { dateTimeDisplayFormat, ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import { UserFollowUpType } from '@/constants';
import { Col, Form, Input, message, Modal, Row, Select } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SaveOutlined } from '@ant-design/icons';
import { apiSdk } from '@/httpClient';
import { defaultApiErrorHandle } from '@/helpers/errorHandler';
import AppDateTimePicker from '@/components/FormItems/DateTimePicker';
import moment from 'moment';

type Props = {
  lianUserId: string;
  onClose: () => void;
};
const filters: Array<IFilterItem> = [
  {
    type: 'TEXT',

    mdSize: 8,
    xlSize: 8,
    xxlSize: 8,
    name: 'comment',
    label: 'passengerAppUser_Comment',
  },
];
const columns: IColumnType<LianeUserFollowUpDetailDto>[] = [
  {
    title: 'passengerAppUser_FollowUpDateTime',
    dataIndex: 'followUpDateTime',
    width: 170,
    render: v => dateTimeDisplayFormat.dateTime(v),
  },
  {
    title: 'passengerAppUser_FollowUpType',
    dataIndex: 'followUpType',
    width: 150,
    render: v => UserFollowUpType.__getValue(v),
  },
  {
    title: 'passengerAppUser_Comment',
    dataIndex: 'comment',
    ellipsis: true,
  },
  {
    title: 'passengerAppUser_NextFollowUpDateTime',
    dataIndex: 'nextFollowUpDateTime',
    width: 170,
    render: v => dateTimeDisplayFormat.dateTime(v),
  },
];

export default function UserFollowupModal({ lianUserId, onClose }: Props) {
  const zTb = React.useRef<ITableRef>();

  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [formVisible, setFormVisible] = React.useState(false);
  const [isNewForm, setIsNewForm] = React.useState(false);

  const createData = values => {
    setLoading(true);
    apiSdk.LianeUserFollowUpDetailService.create({ body: { ...values, lianeUserId: lianUserId } })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(defaultApiErrorHandle)
      .finally(() => {
        setLoading(false);
      });
  };
  const updateData = values => {
    setLoading(true);
    apiSdk.LianeUserFollowUpDetailService.update({ body: { ...values, lianeUserId: lianUserId } })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(defaultApiErrorHandle)

      .finally(() => {
        setLoading(false);
      });
  };
  return (
    <Modal
      open={true}
      maskClosable={false}
      width={ModalWidth.large}
      style={{ top: 10 }}
      className="nta-custom-form"
      title={t('passengerAppUser_UserFollowupsDetails')}
      forceRender={true}
      cancelText={t('Close')}
      okButtonProps={{ style: { display: 'none' } }}
      onCancel={onClose}
      bodyStyle={{ padding: 10 }}
    >
      <ZTable<LianeUserFollowUpDetailDto>
        columns={columns}
        searchBtnAlignLeft
        extraBtns={
          <CreateBtn
            onClick={() => {
              form.resetFields();
              setIsNewForm(true);
              setFormVisible(true);
            }}
          />
        }
        primaryKey="id"
        tableRef={zTb}
        url="/user-follow-up-details/get-follow-up-details"
        bordered
        defaultFilter={{ lianeUserId: lianUserId }}
        filters={filters}
        order_by="createdOn"
        delete_url="/user-follow-up-details/delete"
        order_by_asc={false}
        allowEdit={true}
        onEdit={record => {
          setIsNewForm(false);
          setFormVisible(true);
          form.setFieldsValue({
            ...record,
            followUpDateTime: moment(record.followUpDateTime),
            nextFollowUpDateTime: moment(record.nextFollowUpDateTime),
          });
        }}
        allowDelete={true}
        actionColumnWidth={100}
      />

      <Modal
        visible={formVisible}
        maskClosable={false}
        width={ModalWidth.small}
        className="nta-custom-form"
        title={isNewForm ? t('passengerAppUser_CreateNew') : t('passengerAppUser_Update')}
        forceRender={true}
        okText={t('save')}
        cancelText={t('cancel')}
        okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
        onCancel={() => {
          form.resetFields();
          setFormVisible(false);
        }}
        onOk={() => {
          form
            .validateFields()
            .then(values => {
              if (values.id) {
                updateData(values);
              } else {
                createData(values);
              }
            })
            .catch(info => {});
        }}
      >
        <Form form={form} size="middle" layout="vertical">
          <Form.Item name="id" className="hidden">
            <Input type="hidden" />
          </Form.Item>
          <Row gutter={ROW_GUTTER}>
            <Col md={24}>
              <Form.Item
                label={t('passengerAppUser_FollowUpDateTime')}
                name="followUpDateTime"
                rules={[{ required: true, message: VALIDATE_REQUIRED }]}
              >
                <AppDateTimePicker className="w-100" />
              </Form.Item>
              <Form.Item label={t('passengerAppUser_FollowUpType')} name="followUpType" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Select options={UserFollowUpType.__getSelectOptions()} className="w-100" />
              </Form.Item>
              <Form.Item
                label={t('passengerAppUser_NextFollowUpDateTime')}
                name="nextFollowUpDateTime"
                rules={[{ required: true, message: VALIDATE_REQUIRED }]}
              >
                <AppDateTimePicker className="w-100" />
              </Form.Item>
              <Form.Item label={t('passengerAppUser_Comment')} name="comment" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Input.TextArea />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Modal>
  );
}
